import { matchRoutes } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import type { ReactElement } from 'react';

/**
 * Helpers d'accès aux routes — pas de mapping role→paths à maintenir : on
 * consomme directement la config réelle via `getRouteObjects()`.
 *
 * `routeObjects` est passé en paramètre (et pas importé) pour rester testable
 * sans charger toute l'app (Loadable + import.meta Vite cassent jest).
 */

type AccessibleRolesProps = { accessibleRoles?: string[] };

/** Métadonnée portée par `handle` sur une `<Route>` (cf. routes/index.js). */
type RouteHandle = { hideBottomMenu?: boolean };

// `matchRoutes` renvoie les matches du plus extérieur au plus intérieur.
// `<Elements accessibleRoles>` est porté par les routes GROUPE parent →
// on trouve toujours `accessibleRoles` sur un parent si la route est protégée.
const getAccessibleRolesFromMatches = (matches: ReturnType<typeof matchRoutes>): string[] | null => {
  if (!matches) return null;
  for (const match of matches) {
    const element = match.route.element as ReactElement<AccessibleRolesProps> | undefined;
    const roles = element?.props?.accessibleRoles;
    if (Array.isArray(roles)) return roles;
  }
  return null;
};

/**
 * `true` si `role` peut accéder à `pathname`. Cas de fallback :
 * - pathname inconnu → `true` (les guards en aval décident)
 * - role falsy (race transitoire) → `true`
 */
export const isPathAccessibleToRole = (
  routeObjects: RouteObject[],
  pathname: string,
  role: string | null | undefined
): boolean => {
  if (!role) return true;
  const matches = matchRoutes(routeObjects, pathname);
  const accessibleRoles = getAccessibleRolesFromMatches(matches);
  if (!accessibleRoles) return true;
  return accessibleRoles.includes(role);
};

/**
 * `true` si la route courante demande à masquer la barre de navigation mobile
 * (pages de saisie plein écran : builder de formulaire, remplissage
 * d'événement). Déclaré via `handle={{ hideBottomMenu: true }}` sur la
 * `<Route>` correspondante — pas de liste de paths à maintenir ici.
 */
export const isBottomMenuHiddenForPath = (routeObjects: RouteObject[], pathname: string): boolean => {
  const matches = matchRoutes(routeObjects, pathname);
  if (!matches) return false;
  return matches.some((match) => (match.route.handle as RouteHandle | undefined)?.hideBottomMenu === true);
};
